/**
 * Local Data Store for Label Templates
 *
 * Caches label templates locally for offline printing functionality.
 * All other data (products, items, locations, customers) is fetched fresh from the API.
 */

import type { LabelTemplate, LabelTemplateType } from './api-label-templates';

export type { LabelTemplate, LabelTemplateType };

const STORE_PREFIX = 'local_store_';

class LocalStore {
  private labelTemplates: Map<string, LabelTemplate> = new Map();

  constructor() {
    if (typeof window !== 'undefined') {
      this.loadFromStorage();
    }
  }

  // Load cached label templates from localStorage
  private loadFromStorage(): void {
    try {
      const labelTemplatesRaw = localStorage.getItem(STORE_PREFIX + 'labelTemplates');

      if (labelTemplatesRaw) {
        const arr: LabelTemplate[] = JSON.parse(labelTemplatesRaw);
        this.labelTemplates = new Map(arr.map(t => [t.id, t]));
      }

      // Clear any legacy cached data from localStorage
      localStorage.removeItem(STORE_PREFIX + 'products');
      localStorage.removeItem(STORE_PREFIX + 'items');
      localStorage.removeItem(STORE_PREFIX + 'locations');
      localStorage.removeItem(STORE_PREFIX + 'customers');
      localStorage.removeItem(STORE_PREFIX + 'sync_timestamp');
    } catch (e) {
    }
  }

  // Save label templates to localStorage
  private saveToStorage(): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(STORE_PREFIX + 'labelTemplates', JSON.stringify(Array.from(this.labelTemplates.values())));
    } catch (e) {
    }
  }

  // Get all label templates
  getLabelTemplates(): LabelTemplate[] {
    return Array.from(this.labelTemplates.values());
  }

  // Get label templates by type
  getLabelTemplatesByType(type: LabelTemplateType): LabelTemplate[] {
    return Array.from(this.labelTemplates.values()).filter(t => t.templateType === type);
  }

  // Get default template for a type
  getDefaultLabelTemplate(type: LabelTemplateType): LabelTemplate | null {
    return Array.from(this.labelTemplates.values()).find(t => t.templateType === type && t.isDefault) || null;
  }

  // Sync label templates from API
  async syncLabelTemplates(apiBaseUrl: string, authToken: string): Promise<void> {
    try {
      const response = await fetch(`${apiBaseUrl}/label-templates`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch label templates: ${response.status}`);
      }

      const templates: LabelTemplate[] = await response.json();
      this.labelTemplates = new Map(templates.map(t => [t.id, t]));
      this.saveToStorage();
    } catch (e) {
    }
  }

  // Clear label templates from localStorage
  clear(): void {
    this.labelTemplates.clear();
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORE_PREFIX + 'labelTemplates');
    }
  }
}

// Singleton instance
export const localStore = new LocalStore();
